import { SensorPortCommunicationMode } from "../../Configurations/portConfigs.js";
import { ForeignLookup } from "../commonSubSchemas/foreignLookup.js";
import { SensorClusterAddress } from "../commonSubSchemas/sensorClusterAddress.js";
import { VertexInfo } from "../commonSubSchemas/vertexInfo.js";
import type { SensorClusterConfigKey } from "../sensorCluster/sensorCluster.js";
import { SensorCluster, type SensorClusterOrigin } from "../sensorCluster/sensorCluster.js";
import { SENSOTERRA_DEVICE_COLLECTION_NAME, type SensoterraDevice } from "./sensoterraDevice.js";

/**
 * Derives the SensorCluster that corresponds to this Sensoterra probe's single
 * soil-moisture reading. Returns an empty array if the device has no owner or
 * no node_id yet (e.g. during initial registration before the Sensoterra cloud
 * API assigns an ID).
 *
 * Depth is stored in meters (the dashboard converts to meters before saving).
 */
export const getSensorClusters = (device: SensoterraDevice): SensorCluster[] => {
    if (!device.owner || !device.node_id) return [];

    const depthMeters = device.depth ?? 0;

    const cluster = new SensorCluster();
    Object.assign(cluster, {
        address: new SensorClusterAddress({
            hostReference: new ForeignLookup({
                targetId: device.node_id,
                targetForeignField: "node_id",
                targetCollection: SENSOTERRA_DEVICE_COLLECTION_NAME,
            }),
            portId: "I1",
            communicationMode: SensorPortCommunicationMode.DIGITAL,
            channel: 0,
        }),
        sensorClusterConfigKey: "sensoterra_single_depth" as SensorClusterConfigKey,
        origin: "fixedDevicePort" satisfies SensorClusterOrigin,
        parameters: { depth: depthMeters },
        owner: device.owner,
        connectedZones: device.zone ? [device.zone] : [],
        position: new VertexInfo({ lat: device.position?.lat, lng: device.position?.lng, depth: depthMeters }),
    });

    return [cluster];
};
